/**
 * Card Agenda Component
 * Gère les interactions des cartes d'agenda avec fonctionnalité "voir plus"
 */

class Agenda {
  private modal: HTMLElement | null = null;
  private modalText: HTMLElement | null = null;
  private modalTitle: HTMLElement | null = null;
  private activeCalendarMenu: HTMLElement | null = null;

  constructor() {
    console.log('ContentActuEvent initialized');
    this.init();
  }

  private init(): void {
    this.setupModal();
    this.checkTextOverflow();
    this.bindEvents();
  }

  private setupModal(): void {
    this.modal = document.getElementById('agenda-modal-full');
    this.modalText =
      this.modal?.querySelector('.content-actu-event__modal-text') || null;
    this.modalTitle =
      this.modal?.querySelector('.content-actu-event__modal-title') || null;
  }

  private checkTextOverflow(): void {
    const excerptElements = document.querySelectorAll(
      '.content-actu-event__excerpt'
    );

    excerptElements.forEach((excerpt) => {
      const element = excerpt as HTMLElement;
      const seeMoreButton = element.parentElement?.querySelector(
        '.content-actu-event__see-more'
      ) as HTMLElement;

      if (seeMoreButton) {
        const textContent = element.textContent || '';

        // Vérifier différents types d'ellipses WordPress
        const hasWordPressEllipsis =
          textContent.endsWith('...') ||
          textContent.endsWith('…') ||
          textContent.includes('[...]') ||
          textContent.length > 150; // Fallback pour texte long (plus long pour content-actu-event)

        console.log('Text content:', textContent);
        console.log('Has ellipsis:', hasWordPressEllipsis);

        if (hasWordPressEllipsis) {
          seeMoreButton.style.display = 'inline';
        } else {
          seeMoreButton.style.display = 'none';
        }
      }
    });
  }

  private bindEvents(): void {
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      const seeMoreButton = target.closest(
        '[data-action="see-more"]'
      ) as HTMLElement;
      if (seeMoreButton) {
        e.preventDefault();
        this.handleSeeMoreClick(seeMoreButton);
        return;
      }

      const addCalendarButton = target.closest(
        '[data-action="add-calendar"]'
      ) as HTMLElement;
      if (addCalendarButton) {
        e.preventDefault();
        this.handleAddCalendarClick(addCalendarButton);
        return;
      }

      const calendarOption = target.closest('[data-calendar]') as HTMLElement;
      if (calendarOption) {
        e.preventDefault();
        this.handleCalendarOptionClick(calendarOption);
        return;
      }

      const closeButton = target.closest(
        '[data-action="close-modal"]'
      ) as HTMLElement;
      if (closeButton) {
        e.preventDefault();
        this.closeModal();
        return;
      }

      // Fermer le modal en cliquant sur l'overlay
      if (target.classList.contains('content-actu-event__modal-overlay')) {
        this.closeModal();
        return;
      }

      // Fermer le menu calendrier en cliquant ailleurs
      if (!target.closest('.content-actu-event__actions')) {
        this.closeCalendarMenus();
      }
    });

    // Fermer le modal avec la touche Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (this.modal?.style.display === 'block') {
          this.closeModal();
        } else {
          this.closeCalendarMenus();
        }
      }
    });
  }

  private handleSeeMoreClick(button: HTMLElement): void {
    const fullText = button.getAttribute('data-full-text');
    const cardElement = button.closest('.content-actu-event');
    const titleElement = cardElement?.querySelector(
      '.content-actu-event__title'
    ) as HTMLElement;

    if (fullText && this.modal && this.modalText && this.modalTitle) {
      this.modalText.innerHTML = fullText;
      this.modalTitle.innerHTML =
        titleElement?.textContent || "Détails de l'événement";
      this.openModal();
    }
  }

  private openModal(): void {
    if (this.modal) {
      this.modal.style.display = 'block';
      document.body.style.overflow = 'hidden'; // Empêcher le scroll de la page
    }
  }

  private closeModal(): void {
    if (this.modal) {
      this.modal.style.display = 'none';
      document.body.style.overflow = 'auto'; // Restaurer le scroll de la page
    }
  }

  private handleAddCalendarClick(button: HTMLElement): void {
    const cardElement = button.closest('.content-actu-event');
    const menu = cardElement?.querySelector(
      '.content-actu-event__calendar-menu'
    ) as HTMLElement;

    if (menu) {
      // Fermer les autres menus ouverts
      this.closeCalendarMenus();

      // Ouvrir ce menu
      menu.style.display = 'block';
      this.activeCalendarMenu = menu;
    }
  }

  private handleCalendarOptionClick(option: HTMLElement): void {
    const calendarType = option.getAttribute('data-calendar');
    const cardElement = option.closest('.content-actu-event');
    const addButton = cardElement?.querySelector(
      '[data-action="add-calendar"]'
    ) as HTMLElement;

    if (calendarType && addButton) {
      const eventData = this.extractEventData(addButton);
      const calendarUrl = this.generateCalendarUrl(calendarType, eventData);

      if (calendarUrl) {
        window.open(calendarUrl, '_blank', 'noopener,noreferrer');
      }

      this.closeCalendarMenus();
    }
  }

  private extractEventData(button: HTMLElement): EventData {
    return {
      title: button.getAttribute('data-title') || '',
      date_start: button.getAttribute('data-date-start') || '',
      date_end: button.getAttribute('data-date-end') || '',
      description: button.getAttribute('data-description') || '',
      location: button.getAttribute('data-location') || ''
    };
  }

  private generateCalendarUrl(
    calendarType: string,
    eventData: EventData
  ): string | null {
    try {
      // Si date_end n'est pas fourni, l'événement dure toute la journée de date_start.
      const effectiveDateEnd = eventData.date_end || eventData.date_start;

      // Pour un événement sur plusieurs jours sans heure, on utilise la date de début et de fin (format YYYYMMDD)
      const startDate = eventData.date_start.replace(/-/g, '');
      // Google Calendar : la date de fin doit être le jour suivant pour inclure le dernier jour
      let endDate = effectiveDateEnd.replace(/-/g, '');
      if (calendarType === 'google' && endDate) {
        // Ajouter 1 jour à la date de fin
        const end = new Date(effectiveDateEnd);
        end.setDate(end.getDate() + 1);
        endDate = end.toISOString().slice(0, 10).replace(/-/g, '');
      }
      if (calendarType === 'google') {
        return this.generateGoogleCalendarUrl(eventData, startDate, endDate);
      } else if (calendarType === 'teams') {
        return this.generateMicrosoftCalendarUrl(eventData, 'office');
      } else if (calendarType === 'teams-perso') {
        return this.generateMicrosoftCalendarUrl(eventData, 'live');
      } else if (calendarType === 'apple') {
        this.generateAndDownloadICS(eventData);
      }
    } catch (error) {
      console.error('Erreur lors de la génération du lien calendrier:', error);
    }
    return null;
  }

  // J'ai renommé la méthode pour qu'elle soit plus descriptive.
  private generateAndDownloadICS(eventData: EventData): void {
    // 1. Déterminer le dernier jour de l'événement.
    // Si eventData.date_end n'est pas fourni (null ou undefined),
    // on utilise eventData.date_start. C'est ici que votre cas est géré.
    console.log('Event Data:', eventData);
    const lastDayOfEvent = eventData.date_end
      ? new Date(eventData.date_end)
      : new Date(eventData.date_start);

    console.log('Last day of event:', lastDayOfEvent);
    const dtEndDate = new Date(lastDayOfEvent);
    dtEndDate.setDate(dtEndDate.getDate() + 1); // <-- C'est l'ajustement crucial
    console.log('Adjusted DTEND date:', dtEndDate);

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Cotelub//Evenements//EN
BEGIN:VEVENT
UID:${Date.now()}@votredomaine.com
DTSTAMP:${this._formatICSDate(new Date().toISOString())}
DTSTART;VALUE=DATE:${this._formatICSDate(eventData.date_start)}
DTEND;VALUE=DATE:${this._formatICSDate(dtEndDate.toISOString())}
SUMMARY:${this._escapeICSString(eventData.title)}
DESCRIPTION:${this._escapeICSString(eventData.description ?? '')}
LOCATION:${this._escapeICSString(eventData.location ?? '')}
END:VEVENT
END:VCALENDAR`;

    console.log('ICS Content:', icsContent);
    // Le reste du code déclenche le téléchargement du fichier .ics
    const blob = new Blob([icsContent], {
      type: 'text/calendar;charset=utf-8'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    const fileName = `${eventData.title
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase()}.ics`;
    link.setAttribute('download', fileName);

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private _escapeICSString(str: string): string {
    return str.replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
  }

  private _formatICSDate(dateString: string): string {
    const date = new Date(dateString);
    // Utilise les fonctions UTC pour éviter les problèmes de fuseau horaire
    return [
      date.getUTCFullYear(),
      String(date.getUTCMonth() + 1).padStart(2, '0'),
      String(date.getUTCDate()).padStart(2, '0'),
      'T',
      String(date.getUTCHours()).padStart(2, '0'),
      String(date.getUTCMinutes()).padStart(2, '0'),
      String(date.getUTCSeconds()).padStart(2, '0'),
      'Z'
    ].join('');
  }
  /**
   * Génère un lien de composition d'événement Outlook (Deep Link) pour un événement sur plusieurs jours sans heure.
   * @param eventData Les données de l'événement.
   * @returns L'URL Outlook/Microsoft.
   */
  private generateMicrosoftCalendarUrl(
    eventData: EventData,
    type: 'office' | 'live'
  ): string {
    // Si date_end n'est pas fourni, l'événement dure toute la journée de date_start.
    const effectiveDateEnd = eventData.date_end || eventData.date_start;

    // Format attendu : YYYY-MM-DD pour les événements à la journée entière
    const start = eventData.date_start;

    // Ajouter 1 jour à la date de fin car Outlook utilise une date de fin exclusive
    const endDate = new Date(effectiveDateEnd);
    endDate.setDate(endDate.getDate() + 1);
    const end = endDate.toISOString().slice(0, 10);

    const params = new URLSearchParams({
      subject: eventData.title,
      body: eventData.description ?? '',
      location: eventData.location ?? '',
      startdt: start,
      enddt: end,
      allday: 'true',
      path: '/calendar/action/compose'
    });

    return `https://outlook.${type}.com/calendar/action/compose?${params.toString()}`;
  }

  private generateGoogleCalendarUrl(
    eventData: EventData,
    startDate: string,
    endDate: string
  ): string {
    // Google Calendar : format all-day event : YYYYMMDD/YYYYMMDD
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: eventData.title,
      dates: `${startDate}/${endDate}`,
      location: eventData.location ?? '',
      details: eventData.description ?? ''
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  }

  private closeCalendarMenus(): void {
    const menus = document.querySelectorAll(
      '.content-actu-event__calendar-menu'
    );
    menus.forEach((menu) => {
      (menu as HTMLElement).style.display = 'none';
    });
    this.activeCalendarMenu = null;
  }
}

interface EventData {
  title: string;
  date_start: string;
  date_end?: string;
  description?: string;
  location?: string;
}

// Initialiser quand le DOM est prêt
document.addEventListener('DOMContentLoaded', () => {
  new Agenda();
});

// Export pour utilisation externe si nécessaire
export default Agenda;
